import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Shield, Clock, Server, Database, HeadphonesIcon, 
  AlertTriangle, CheckCircle2, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';

const SLAPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <FileText className="h-4 w-4" />
            Service Level Agreement
          </div>
          <h1 className="font-display text-4xl font-bold mb-4">
            SLA für Managed Nextcloud Hosting
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Transparente Qualitätsstandards, garantierte Verfügbarkeit und klare Reaktionszeiten – 
            dein Vertrauen ist uns wichtig.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">99,9%</div>
            <div className="text-sm text-muted-foreground">Verfügbarkeit</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">&lt;2h</div>
            <div className="text-sm text-muted-foreground">Reaktionszeit Prio 1</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">24h</div>
            <div className="text-sm text-muted-foreground">Security Updates</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">14 Tage</div>
            <div className="text-sm text-muted-foreground">Backup-Vorhaltung</div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                1. Gegenstand und Geltungsbereich
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate dark:prose-invert max-w-none">
              <p>
                Dieses Service Level Agreement (SLA) beschreibt die Qualitätsstandards, 
                Verfügbarkeiten und Reaktionszeiten für die vom Anbieter bereitgestellten 
                Managed Nextcloud Hosting-Dienste. Es regelt die Ansprüche des Kunden bei 
                Nichteinhaltung dieser Standards.
              </p>
            </CardContent>
          </Card>

          {/* Section 2 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" />
                2. Verfügbarkeit der Dienste
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">2.1 Garantierte Verfügbarkeit</h4>
                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="font-bold text-lg text-green-700 dark:text-green-400">99,9 % Verfügbarkeit</p>
                    <p className="text-sm text-green-600 dark:text-green-500">im Jahresmittel garantiert</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">2.2 Messverfahren und Referenz-Domain</h4>
                <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Wichtiger Hinweis</p>
                      <p className="text-amber-600 dark:text-amber-500">
                        Die Verfügbarkeit wird <strong>ausschließlich</strong> anhand der technischen Service-Domain 
                        (Notfall-Domain, z. B. <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">kundenID.dein-service.de</code>) gemessen.
                      </p>
                    </div>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span><strong>Bring Your Own Domain (BYOD):</strong> Nutzt der Kunde eine eigene Domain (externe DNS-Verwaltung), 
                    liegt die Verantwortung für die korrekte DNS-Konfiguration beim Kunden.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span><strong>Haftungsausschluss bei DNS-Fehlern:</strong> Ist die Nextcloud-Instanz über die technische 
                    Service-Domain erreichbar, gilt der Dienst als zu 100 % verfügbar.</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Section 3 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                3. Wartung und Updates (Managed Service)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">3.1 Updates und Patches</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="font-semibold text-red-700 dark:text-red-400 mb-1">🔒 Sicherheitsupdates</p>
                    <p className="text-sm text-red-600 dark:text-red-500">
                      Kritische Sicherheitslücken werden innerhalb von <strong>24 Stunden</strong> nach 
                      Freigabe und Prüfung geschlossen.
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="font-semibold text-blue-700 dark:text-blue-400 mb-1">🚀 Versions-Upgrades</p>
                    <p className="text-sm text-blue-600 dark:text-blue-500">
                      Major-Updates werden nach interner Stabilitätsprüfung eingespielt 
                      (<strong>4–8 Wochen</strong> nach offiziellem Release).
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">3.2 Wartungsfenster</h4>
                <p className="text-sm text-muted-foreground">
                  Planmäßige Wartungsarbeiten werden vorzugsweise in lastarmen Zeiten durchgeführt 
                  (<strong>22:00 Uhr bis 06:00 Uhr MEZ</strong>). Diese angekündigten Wartungsarbeiten 
                  zählen nicht als Ausfallzeit bei der Berechnung der Verfügbarkeit.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 4 - Support */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HeadphonesIcon className="h-5 w-5 text-primary" />
                4. Support und Reaktionszeiten
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Der Support erfolgt über das Ticket-System / E-Mail. Die Reaktionszeit definiert die Zeitspanne 
                zwischen dem Eingang der Meldung und der ersten qualifizierten Rückmeldung eines Technikers (keine Auto-Reply).
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold">Priorität</th>
                      <th className="text-left py-3 px-4 font-semibold">Beschreibung</th>
                      <th className="text-left py-3 px-4 font-semibold">Reaktionszeit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-red-50/50 dark:bg-red-950/20">
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-2 py-1 rounded text-xs font-semibold">
                          Prio 1 – Kritisch
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        Totalausfall der Instanz; Instanz über Notfall-Domain nicht erreichbar; Drohender Datenverlust.
                      </td>
                      <td className="py-3 px-4 font-bold text-red-600">&lt; 2 Std.</td>
                    </tr>
                    <tr className="border-b bg-orange-50/50 dark:bg-orange-950/20">
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-1 rounded text-xs font-semibold">
                          Prio 2 – Hoch
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        Instanz läuft, aber wesentliche Funktionen (z. B. Login, Sync, Talk) sind massiv gestört.
                      </td>
                      <td className="py-3 px-4 font-bold text-orange-600">&lt; 8 Std.</td>
                    </tr>
                    <tr className="bg-blue-50/50 dark:bg-blue-950/20">
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded text-xs font-semibold">
                          Prio 3 – Normal
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        Allgemeine Anfragen, Konfigurationswünsche, Performance-Fragen, unkritische Bugs.
                      </td>
                      <td className="py-3 px-4 font-bold text-blue-600">&lt; 24 Std. (Mo-Fr)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="text-xs text-muted-foreground mt-4 italic">
                *Die Reaktionszeiten gelten innerhalb der regulären Geschäftszeiten (Mo – Fr, 09:00 – 17:00 Uhr, ausgenommen Feiertage).
              </p>
            </CardContent>
          </Card>

          {/* Section 5 - Backup */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                5. Datensicherung (Backup)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-semibold">Zyklus</p>
                      <p className="text-sm text-muted-foreground">
                        Täglich (<strong>alle 24 Stunden</strong>) vollumfängliches Backup der Datenbank und aller Nutzerdaten.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Database className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-semibold">Aufbewahrung</p>
                      <p className="text-sm text-muted-foreground">
                        Backups werden für <strong>14 Tage</strong> vorgehalten.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Server className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-semibold">Speicherort</p>
                      <p className="text-sm text-muted-foreground">
                        Georedundant oder physisch getrennt vom Produktivsystem.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-semibold">Restore</p>
                      <p className="text-sm text-muted-foreground">
                        Wiederherstellung bei technischen Fehlern kostenfrei. Restore auf Kundenwunsch nach Aufwand.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 6 - Gutschriften */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                6. Nichteinhaltung und Gutschriften
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Wird die in Punkt 2.1 genannte Verfügbarkeit (gemessen an der Notfall-Domain) im Jahresmittel unterschritten, 
                erhält der Kunde auf schriftlichen Antrag eine Gutschrift auf die monatliche Grundgebühr:
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <span className="text-sm">Verfügbarkeit <strong>&lt; 99,9 %</strong></span>
                  <span className="font-bold text-yellow-700 dark:text-yellow-400">10 % Gutschrift</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800">
                  <span className="text-sm">Verfügbarkeit <strong>&lt; 99,0 %</strong></span>
                  <span className="font-bold text-orange-700 dark:text-orange-400">25 % Gutschrift</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                  <span className="text-sm">Verfügbarkeit <strong>&lt; 98,0 %</strong></span>
                  <span className="font-bold text-red-700 dark:text-red-400">50 % Gutschrift</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 7 - Ausschlüsse */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-primary" />
                7. Ausschlüsse (Haftungsbeschränkung)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Der Anbieter übernimmt keine Gewährleistung für die Verfügbarkeit in folgenden Fällen:
              </p>
              
              <ol className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">1.</span>
                  <span>Umstände höherer Gewalt (Feuer, Flut, Streik, Internet-Backbone-Ausfälle).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">2.</span>
                  <span>Störungen, die durch Drittanbieter-Apps (Nextcloud Apps, die nicht zum offiziellen "Supported"-Stack des Anbieters gehören) verursacht werden.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">3.</span>
                  <span>Sperrung der Instanz aufgrund von Missbrauch (Abuse) oder Vertragsverletzungen durch den Kunden.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">4.</span>
                  <span>Kunden-Hardware oder Softwareprobleme auf Client-Seite (Browser, Desktop-Client).</span>
                </li>
              </ol>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center pt-8">
            <p className="text-muted-foreground mb-4">
              Hast du Fragen zu unserem SLA? Wir sind für dich da!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <Link to="/checkout">
                  Jetzt Wolke erstellen
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <a href="mailto:support@wolkenhelden.de">
                  Support kontaktieren
                </a>
              </Button>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default SLAPage;
